import { Radio, Users } from 'lucide-react'
import type React from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import type { ChatConversation, ChatUser } from '../../types/chat'
import { getAvatarUrl } from '../../utils/avatarUtils'
import { formatDistanceToNow } from '../../utils/dateUtils'

interface ChatConversationItemProps {
  conversation: ChatConversation
  otherUser: ChatUser | null
  isActive: boolean
  onClick: () => void
}

const ConversationItem = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  background-color: ${(props) => (props.isActive ? theme.colors.surfaceHover : 'transparent')};
  transition: ${theme.transitions.default};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }
`

const Avatar = styled.div<{ online?: boolean; type?: string; $chatId?: string; $isActive?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme, type }) =>
    type === 'broadcast'
      ? 'rgba(255, 107, 53, 0.1)'
      : type === 'group'
        ? 'rgba(78, 205, 196, 0.1)'
        : theme.colors.surface};
  margin-right: ${theme.spacing.md};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: ${({ theme, type }) =>
    type === 'broadcast' ? '#ff6b35' : type === 'group' ? '#4ecdc4' : theme.colors.primary};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
  contain: layout;
  view-transition-name: ${({ $chatId, $isActive }) => ($chatId && !$isActive ? `chat-avatar-${$chatId}` : 'none')};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::after {
    content: '';
    display: ${(props) => (props.online ? 'block' : 'none')};
    width: 10px;
    height: 10px;
    background-color: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    position: absolute;
    bottom: 0;
    right: 0;
    border: 2px solid ${({ theme }) => theme.colors.background};
  }
`

const ConversationInfo = styled.div`
  flex: 1;
  overflow: hidden;
`

const ConversationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xs};
`

const UserName = styled.span<{ $chatId?: string; $isActive?: boolean }>`
  font-weight: bold;
  color: ${({ theme }) => theme.colors.text};
  contain: layout;
  view-transition-name: ${({ $chatId, $isActive }) => ($chatId && !$isActive ? `chat-name-${$chatId}` : 'none')};
`

const TimeStamp = styled.span`
  font-size: ${theme.typography.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

const LastMessage = styled.div`
  font-size: ${theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const UnreadBadge = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.typography.xs};
  margin-left: ${theme.spacing.sm};
`

export const ChatConversationItem: React.FC<ChatConversationItemProps> = ({
  conversation,
  otherUser,
  isActive,
  onClick,
}) => {
  const { lastMessage, unreadCount, type, name } = conversation

  const getDisplayName = () => {
    if (type === 'direct' && otherUser) {
      return otherUser.name
    }
    return name || 'Unknown Chat'
  }

  const getAvatarContent = () => {
    // For direct chats, check if user has avatar
    if (type === 'direct' && otherUser) {
      if (otherUser.avatar) {
        const avatarUrl = getAvatarUrl(otherUser.avatar)
        if (avatarUrl) {
          return <img src={avatarUrl} alt={otherUser.name} />
        }
      }
      return otherUser.name.charAt(0)
    }

    // For broadcast/group chats, show icons
    if (type === 'broadcast') {
      return <Radio size={20} />
    }
    if (type === 'group') {
      return <Users size={20} />
    }

    return '?'
  }

  return (
    <ConversationItem isActive={isActive} onClick={onClick}>
      <Avatar
        online={otherUser?.online}
        type={type}
        $chatId={type === 'direct' ? conversation.id : undefined}
        $isActive={isActive}
      >
        {getAvatarContent()}
      </Avatar>
      <ConversationInfo>
        <ConversationHeader>
          <UserName $chatId={type === 'direct' ? conversation.id : undefined} $isActive={isActive}>
            {getDisplayName()}
          </UserName>
          <TimeStamp>{formatDistanceToNow(new Date(lastMessage.timestamp))}</TimeStamp>
        </ConversationHeader>
        <LastMessage>{lastMessage.content}</LastMessage>
      </ConversationInfo>
      {unreadCount > 0 && <UnreadBadge>{unreadCount}</UnreadBadge>}
    </ConversationItem>
  )
}
